import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BottomIllustration from '../assets/competing-bottom.svg';
import LeftIllustration from '../assets/competing-left.svg';
import RightIllustration from '../assets/competing-right.svg';

import { Label, Title } from './BenefitsOverviewSlice';
import { Index, Percentage, Section, BlockTitle } from './WorkBenefitSlice';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, desktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const SectionWrapper = styled(Section)`
  && {
    background-image: var(--colors-zircon);
  }
`;

const BenefitBlock = styled.div`
  padding: 40px 5px;
  border-top: 1px solid var(--secondaryColor);
  display: flex;
  align-items: baseline;

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}
`;

export const TitleWrapper = styled(Title)`
  && {
    color: var(--secondaryColor);
    margin-bottom: 50px;
  }
`;

export const IndexWrapper = styled(Index)`
  && {
    color: var(--secondaryColor);
    margin-left: 15px;
  }
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.titleSM}
  color: ${({ theme }) => theme.colors.darkBlue_75};
  font-weight: 400;
  max-width: 700px;
`;

const SecondParagraph = styled.div`
  display: flex;
  align-items: center;

  ${tabletBreakpoint(css`
    flex-direction: column;
    align-items: flex-start;
  `)}
`;

const PercentageWrapper = styled(Percentage)`
  && {
    color: var(--secondaryColor);
    margin-right: 20px;

    ${tabletBreakpoint(css`
      margin-top: 20px;
    `)}

    ${whenProp(
      '$blueBg',
      css`
        background: var(--secondaryColor);
        padding: 0 2px;
        border-radius: 8px;
        color: var(--colors-blank);
      `
    )}
  }
`;

const LeftImageWrapper = styled.div`
  position: absolute;
  top: 45%;
  left: 0;
  z-index: 0;

  ${desktopBreakpoint(css`
    display: none;
  `)}
`;

const RightImageWrapper = styled(LeftImageWrapper)`
  left: unset;
  top: 0;
  right: -5%;
`;

const BottomImageWrapper = styled(RightImageWrapper)`
  && {
    top: unset;
    bottom: -8%;
    right: -5%;
  }
`;

const BlockContent = styled.div`
  margin-left: 30px;

  ${tabletBreakpoint(css`
    margin-left: 15px;
  `)}
`;

export function PercentageWithDescription({ percentage, percentageDescription, bluePercentage }) {
  return (
    <SecondParagraph>
      {fieldHasContent(percentage) && (
        <PercentageWrapper $blueBg={bluePercentage}>
          <PrismicRichText field={percentage} />
        </PercentageWrapper>
      )}
      {fieldHasContent(percentageDescription) && (
        <Description>
          <PrismicRichText field={percentageDescription} />
        </Description>
      )}
    </SecondParagraph>
  );
}

export function CompetingTalentSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { label, title } = primary;

  return (
    <SectionWrapper>
      <ContentContainer>
        {fieldHasContent(label) && <Label>{label}</Label>}
        {fieldHasContent(title) && (
          <TitleWrapper>
            <PrismicRichText field={title} />
          </TitleWrapper>
        )}
        <div>
          {fields.map(
            (
              {
                title: blockTitle,
                description,
                percentage,
                percentageDescription,
                bluePercentage,
                secondPercentage,
                secondPercentageDescription,
                thirdPercentage,
                thirdPercentageDescription,
                additionalDescription,
              },
              index
            ) => (
              <BenefitBlock key={index}>
                <IndexWrapper>0{index + 1}</IndexWrapper>
                <BlockContent>
                  {fieldHasContent(blockTitle) && (
                    <BlockTitle>
                      <PrismicRichText field={blockTitle} />
                    </BlockTitle>
                  )}
                  {fieldHasContent(description) && (
                    <Description>
                      <PrismicRichText field={description} />
                    </Description>
                  )}
                  {percentage && percentageDescription && (
                    <PercentageWithDescription
                      percentage={percentage}
                      bluePercentage={bluePercentage}
                      percentageDescription={percentageDescription}
                    />
                  )}
                  {secondPercentage && secondPercentageDescription && (
                    <PercentageWithDescription
                      percentage={secondPercentage}
                      percentageDescription={secondPercentageDescription}
                    />
                  )}
                  {thirdPercentage && thirdPercentageDescription && (
                    <PercentageWithDescription
                      percentage={thirdPercentage}
                      percentageDescription={thirdPercentageDescription}
                    />
                  )}
                  {fieldHasContent(additionalDescription) && (
                    <Description>
                      <PrismicRichText field={additionalDescription} />
                    </Description>
                  )}
                </BlockContent>
              </BenefitBlock>
            )
          )}
        </div>
      </ContentContainer>
      <LeftImageWrapper>
        <LeftIllustration />
      </LeftImageWrapper>
      <RightImageWrapper>
        <RightIllustration />
      </RightImageWrapper>
      <BottomImageWrapper>
        <BottomIllustration />
      </BottomImageWrapper>
    </SectionWrapper>
  );
}
